<template>
    <article>
        <p>
            At Coroot, we work on a product that detects the root cause of a service outage and provides our users with a
            list of possible fixes. Databases are crucial parts of any infrastructure, so our product must be able to identify common pitfalls in this area.
            According to numerous database rankings, Postgres is the most popular database these days, so it made sense to start there.
        </p>

        <p>
            Initially, we planned to use metrics collected by existing open-source Prometheus exporters, such as <a
            href="https://github.com/prometheus-community/postgres_exporter" target="_blank">postgres_exporter</a> (1.7k stars on Github).
            Unfortunately, we realized that the metrics it collects are useless for identifying the most common Postgres issues, like:
        </p>

        <ul>
            <li>A CPU-intensive query is affecting other queries</li>
            <li>A server has reached its <var>max_connections</var> limit because most of the connections are stuck in the <var>idle in transaction</var> state</li>
            <li>An I/O-intensive query is affecting other queries</li>
            <li>A query is blocking other queries by holding an exclusive lock</li>
        </ul>

        <h2>Pg-agent (Apache 2.0)</h2>

        <p>
            Proposed changes to <var>postgres_exporter</var> would be too massive, so we decided to
            build another exporter focusing on query performance statistics &mdash;
            <a href="https://github.com/coroot/coroot-pg-agent" target="_blank">pg-agent</a>. It's open-source and
            distributed under the Apache 2.0 license.
        </p>

        <p>
            While working on pg-agent, we reproduce the failure scenarios mentioned above and check whether
            the collected metrics identify a particular failure or not.
            Here are a few things we realized while testing the agent on real failures:
        </p>

        <ul>
            <li>
                statistics from <var>pg_stat_activity</var> and <var>pg_stat_statements</var> should be merged by the query
                to take into account not only finished queries but also those still executing
            </li>
            <li>
                queries from <var>pg_stat_activity</var> should be obfuscated and normalized, since they contain raw query parameters
            </li>
            <li>
                queries from <var>pg_stat_statements</var> should also be obfuscated and normalized to aggregate statistics of similar queries with
                different numbers of elements in the <var>ANY (...)</var> or <var>IN (...)</var> expressions
            </li>
            <li>
                it should be possible to find out the latest queries for the <var>active</var> and <var>idle in transaction</var> connections.
            </li>
            <li>
                to deal with issues related to lock contentions, we need to know not only the queries that are waiting for locks but also those holding these locks
            </li>
        </ul>

        <p>
            Let's take a look at the resulting metrics and how they can help you deal with Postgres-related issues.
        </p>

        <h3>CPU-intensive queries</h3>

        <p>
            The <MetricLink name="pg_top_query_time_per_second" exporter="pg-agent"/> metric is quite useful when you need to find
            the most CPU-intensive queries. This metric is a combination of <var>clock_timestamp()-pg_stat_activity.query_start</var>
            and <var>pg_stat_statements.total_time</var> for a particular query.
        </p>

        <pre v-hl.sh>
topk(10, pg_top_query_time_per_second{rds_instance="us-west-1/pg-main-1", db="products"})
        </pre>


        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg-agent/cpu.png" />
        </v-card>


        <h3>I/O-intensive queries</h3>

        <p>
            The <MetricLink name="pg_top_query_io_time_per_second" exporter="pg-agent"/> metric helps to
            investigate I/O issues because it makes it easy to find the most I/O-intensive Postgres queries.
        </p>

        <pre v-hl.sh>
topk(10, pg_top_query_io_time_per_second{rds_instance="us-west-1/pg-main-1", db="products"})
        </pre>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg-agent/io.png" />
        </v-card>



        <h3>Client connections</h3>

        <p>
            The number of client connections that a Postgres server can handle is always limited by its settings (<var>max_connections</var>).
            This is because Postgres forks a new process for each client connection. Every process consumes memory and CPU resources, even when idle.
            If the number of connections reaches <var>max_connections</var>, Postgres will reject clients' attempts to connect.
        </p>

        <p>
            The <MetricLink name="pg_connections" exporter="pg-agent"/> metric is the number of connections broken down by their
            <var>state</var>, <var>db</var>, <var>user</var>, <var>wait_event_type</var>, and <var>query</var>.
            Such metric cardinality allows you to drill down and see the most recent queries executed within the <var>active</var> or <var>idle</var> transactions.
        </p>

        <p>
            For example, if a Postgres server is rejecting connections, we can aggregate connections by their states:
        </p>

        <pre v-hl.sh>
sum by(state) (pg_connections{rds_instance="us-west-1/pg-main-1"})
        </pre>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg-agent/connections.png" />
        </v-card>

        <p>
            As seen above, most connections are in the <var>idle in transaction</var> state.
            Now, we can drill down to see the last executed queries before the transactions became idle:
        </p>

        <pre v-hl.sh>
sum by(query) (pg_connections{rds_instance="us-west-1/pg-main-1", state="idle in transaction"})
        </pre>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg-agent/idle-in-transaction.png" />
        </v-card>

        <p>
            By understanding which transactions are in the <var>idle in transaction</var> state, we can figure out
            the cause of this behavior.
            For example, in this scenario, the application communicated with a remote service within transactions.
        </p>


        <h3>Locks</h3>

        <p>
            To ensure data consistency, all databases use locks. The waiting time to acquire a lock is usually negligible,
            but there still may be cases where locking is an issue.
            To find the queries waiting for locks we can use the <MetricLink name="pg_connections" exporter="pg-agent"/> metric:
        </p>

        <pre v-hl.sh>
sum by(query) (pg_connections{rds_instance="us-west-1/pg-main-1", state="active", wait_event_type="Lock"})
        </pre>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg-agent/locks.png" />
        </v-card>

        <p>
            It is now clear that the issue is caused by lock contention.
            The next step is to figure out which query is holding the lock.
            To do so, we can use the <MetricLink name="pg_lock_awaiting_queries" exporter="pg-agent"/> metric.
            This shows the number of queries awaiting the lock that are being held by a particular query.
            This metric is gathered by calling <var>pg_blocking_pids()</var> for every locked query and then
            identifying the relevant query for the returned <var>PID</var>.
        </p>

        <pre v-hl.sh>
topk(10, pg_lock_awaiting_queries{rds_instance="us-west-1/pg-main-1"})
        </pre>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg-agent/blocking-queries.png" />
        </v-card>


        <h2>Metric cardinality</h2>

        <p>
            The main concern about using queries as metric labels is the cardinality of the resulting metrics.
            Handling high cardinality metrics are challenging for most Time-Series Databases (TSDBs).
            Pg-agent addresses this problem in the following ways:
        </p>

        <ul>
            <li>
                Aggregating statistics of similar queries, e.g., those that differ only in the number of elements
                in the <var>ANY (...)</var> or <var>IN (...)</var> expressions. <br/>
                On the one hand, the execution plan of such queries may vary depending on the number of elements.
                However, <var>pg_stat_statements</var> and <var>pg_stat_activity</var> do not contain any <var>plan_id</var>
                that could be used along with queries for grouping, so we aggregate statistics only by a normalized query.
            </li>
            <li>
                Since, in most situations, we only need to find the major impacting queries, pg-agent collects statistics only for the top 20
                by total execution time.
            </li>
        </ul>


        <h2>Quick start</h2>

        <p>
            Pg-agent can gather metrics from Postgres servers starting from version 9.6.
            It requires a database user with the assigned <var>pg_monitor</var> role.
            You will also need to enable the <var>pg_stat_statement</var> extension.
        </p>

        <pre v-hl.sql>
create role &lt;USER&gt; with login password '&lt;PASSWORD&gt;';
grant pg_monitor to &lt;USER&gt;
create extension pg_stat_statements;
        </pre>

        <p>
            Then, run the agent:
        </p>

        <pre v-hl.sh>
docker run --detach --name coroot-pg-agent \
  --env DSN="postgresql://&lt;USER&gt;:&lt;PASSWORD&gt;@&lt;HOST&gt;:5432/postgres?connect_timeout=1&statement_timeout=30000" \
  ghcr.io/coroot/coroot-pg-agent
        </pre>


        <p>Flags:</p>

        <pre v-hl.sh>
usage: coroot-pg-agent [&lt;flags&gt;] &lt;dsn&gt;

Flags:
      --listen="0.0.0.0:80"  Listen address (env: LISTEN) - "&lt;ip&gt;:&lt;port&gt;" or ":&lt;port&gt;".
      --scrape-interval=15s  How often to snapshot system views (env: PG_SCRAPE_INTERVAL).
      --version              Show application version.

Args:
  &lt;dsn&gt;  Data source name (env: DSN) - "postgresql://&lt;user&gt;:&lt;password&gt;@&lt;host&gt;:5432/postgres?connect_timeout=1&statement_timeout=30000".
        </pre>

        <h3>Kubernetes</h3>

        <p>
            If you run Postgres in a Kubernetes cluster, you can deploy pg-agent as a sidecar container.
            Here is an example of the <var>sidecars</var> spec section
            for the <a href="https://github.com/zalando/postgres-operator" target="_blank">Postgres Operator by Zalando</a>:
        </p>

        <pre v-hl.yaml>
...
podAnnotations:
  prometheus.io/scrape: 'true'
  prometheus.io/port: '80'
sidecars:
- name: pg-agent
  image: "ghcr.io/coroot/coroot-pg-agent:1.0.5"
  ports:
    - containerPort: 80
      protocol: TCP
  resources:
    requests:
      cpu: 100m
      memory: 100Mi
  env:
    - name: PGUSER
      valueFrom:
        secretKeyRef:
          name: postgres.product-pg.credentials
          key: username
    - name: PGPASSWORD
      valueFrom:
        secretKeyRef:
          name: postgres.product-pg.credentials
          key: password
    - name: DSN
      value: postgresql://$(PGUSER):$(PGPASSWORD)@127.0.0.1:5432/postgres
        </pre>

        <h3>Amazon RDS for Postgres</h3>

        <p>
            <a href="https://github.com/coroot/coroot-aws-agent" target="_blank">Aws-agent</a> is a dedicated agent
            that discovers running Postgres & Aurora for Postgres RDS instances in a specified AWS region and instruments them with built-in pg-agent.
            In addition to Postgres metrics, it gathers:
        </p>

        <ul>
            <li>OS metrics (based on <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank">Enhanced Monitoring</a>)</li>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'metric-exporters', group: 'aws-agent', item: 'metrics'}, hash:'#aws_rds_log_messages_total'}" target='_blank'>Log-based metrics</router-link></li>
            <li><router-link :to="{name: 'docs', params: {chapter: 'metric-exporters', group: 'aws-agent', item: 'metrics'}, hash:'#aws_rds_info'}" target='_blank'>Meta information</router-link> of the RDS instance</li>
        </ul>

        <p>
            You can find step-by-step instructions on how to install aws-agent and set up an IAM role for it
            on <a href="https://github.com/coroot/coroot-aws-agent" target="_blank">GitHub</a>.
        </p>

        <h2>Conclusion</h2>

        <p>
            We hope that the metrics collected by pg-agent and aws-agent will make troubleshooting Postgres-related issues easier
            not only for experienced DBAs but also for engineers not specialized in databases.
        </p>

        <ul>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Get started with Coroot</router-link>
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-pg-agent" target="_blank">pg-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-aws-agent" target="_blank">aws-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
        </ul>
    </article>
</template>

<script>
import MetricLink from "@/components/MetricLink";

export default {
    components: {MetricLink}
}
</script>

<style scoped>
</style>
